import { Injectable, OnDestroy } from '@angular/core';
import { GenericNameByCultureIDResponse } from '../../../models/dtos/Classes/GenericNameByCultureIDResponse';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { ReportCheckboxes } from '../../../models/reports/ReportCheckboxes';
import { Subject, firstValueFrom, take } from 'rxjs';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ReportsAPIService } from '../../api/reports-api.service';
import { GenericNameByCultureIDRequest } from '../../../models/dtos/Classes/GenericNameByCultureIDRequest';
import { MultiPdfReportGroup, reportSequenceToKeys, reportSequenceToIds, reportKeysToSequence } from '../../../models/reports/MultiPdfReportGroups';
import { ReportEnginePayload } from '../../../models/reports/ReportEnginePayload';
import { LoadReportGroupPayload } from '../../../models/reports/LoadReportGroupPayload';
import { SaveReportGroup } from '../../../models/reports/SaveReportGroup';
import { MultiPdfReportData } from '../../../models/reports/MultiPdfReportData';
import { environment } from '../../../../environments/environment';
import { BusinessTypes, GetMultiPdfReportsRequest, Material, SetUserPreferencesRequest, SubmittalPackage } from '../../../models/dtos';
import { CommonDataService } from '../../common-data.service';
import { Preferences } from '../../../models/dtos/Classes/Preferences';
import { EPartsMaterial } from '../../../models/dtos/Classes/EPartsMaterial';
import { MaterialIndicators } from '../../../models/MaterialIndicators';
import { MultiPdfTemplate } from '../../../models/dtos/Classes/MultiPdfTemplate';
import { ReportsType } from '../../../models/dtos/Enums/ReportsType';
import { SaveOrSaveAsMultiPdfTemplateRequest } from '../../../models/dtos/Classes/SaveOrSaveAsMultiPdfTemplateRequest';
import { DeleteMultiPdfTemplateRequest } from '../../../models/dtos/Classes/DeleteMultiPdfTemplateRequest';
import { PreferenceApiService } from '../../api/preference.service';
import { UserProfileService } from '../../common/user-profile.service';
import { reportItems } from '../../../models/reports/ReportNameList';
import { REPORT_CHECKBOX_DISABLE_MAP } from '../../../models/reports/ReportCheckboxDisableMap';
import { MULTI_PDF_REPORT_KEYS } from '../../../models/reports/MultiPdfReportKeys';
import { CommonMetaDataService } from '../../common-meta-data.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonFunctionsService } from '../../common-functions.service';
import { IData } from '../../../models/reports/IData';
import { IHeader } from '../../../models/reports/IHeader';
import { ReportHeaderModel } from '../../../models/reports/ReportHeaderModel';
import { IReportItem } from '../../../models/reports/IReportItem';
import { ISubmittalPackages } from '../../../models/properties/ISubmittalPackages';
import { LoadingSpinnerService } from '../../common/loading-spinner.service';

@Injectable({
  providedIn: 'root'
})
export class ReportsCommonService implements OnDestroy {
  private reportLoading = false;

  private readonly checklistReportByBusiness: Record<string, string> = {
    [CONSTANTS.BUSINESS_FIRE]: 'CHECKLIST_FIRE',
    [CONSTANTS.BUSINESS_ELECTRICAL]: 'CHECKLIST_ELECTRICAL',
    [CONSTANTS.BUSINESS_AUTOMATION]: 'CHECKLIST_AUTOMATION',
    [CONSTANTS.BUSINESS_SECURITY]: 'CHECKLIST_SECURITY_REPORT',
    [CONSTANTS.BUSINESS_MECHANICAL]: 'CHECKLIST_MECHANICAL'
  };

  constructor(
    private readonly reportsApiService: ReportsAPIService,
    private readonly commonDataService: CommonDataService,
    private readonly preferenceApiService: PreferenceApiService,
    private readonly userProfileService: UserProfileService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly translate: TranslateService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly loadingSpinnerService: LoadingSpinnerService
  ) {
    this._reportCheckBoxChanged.next(this.getInitialReportCheckboxes());
  }

  startReportLoading(message: string): void {
    if (this.reportLoading) {
      return;
    }

    this.reportLoading = true;
    this.loadingSpinnerService.updateMessage(message);
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);
  }

  stopReportLoading(): void {
    if (!this.reportLoading) {
      return;
    }

    this.reportLoading = false;
    this.loadingSpinnerService.updateMessage('');
    this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
  }

  generateReportTitles(reportHeaderData: ReportHeaderModel): IData[] {
    return [
      {
        data: [
          { data: this.translate.instant('REVIEW_HEADER_SALES_PERSON') + ':', isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: reportHeaderData.salesPersonName, isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: this.translate.instant('PRINT_DATE') + ':', isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: this.formatDateForReport(new Date()), isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 }
        ]
      },
      {
        data: [
          { data: this.translate.instant('ESTIMATE_NAME') + ':', isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: reportHeaderData.estimateName, isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: this.translate.instant('JOB_START') + ':', isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: reportHeaderData.jobStart, isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 }
        ]
      },
      {
        data: [
          { data: this.translate.instant('ESTIMATE_ID') + ':', isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: reportHeaderData.estimateId, isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: this.translate.instant('JOB_END') + ':', isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 },
          { data: reportHeaderData.jobEnd, isSubHeader: false, columnspan: 1, DataCellColor: "#f0f0f0", hierarchyLevel: 0 }
        ]
      }
    ];
  }

  formatDateForReport(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  generateReportTableHeader(name: string, itemName?: string): IHeader[] {
    return [
      {
        headers: [
          { headerText: this.translate.instant(name) + (itemName ? ': ' + itemName : ''), columnspan: 4 }
        ]
      }
    ];
  }

  public getReportSequence(multiPdfId: string): number[] {
    const multiPdfTemplates = this.commonMetaDataService.getMetaData()?.multiPDFTemplates.filter(x => x.cultureId === this.commonDataService.getEstimateCultureId()) ?? [];
    const localTemplates = this.commonDataService.getUserData()?.multiPdfLocalLibraryReports?.multiPdfLocalLibraryReportsList ?? [];
    const template = [...multiPdfTemplates, ...localTemplates].find(t => t.id.toLowerCase() === multiPdfId.toLowerCase());
    const sequenceStr = template?.reportSequence ?? '';
    const reportSequence: number[] = sequenceStr
      .split(',')
      .filter(value => value.trim() !== '')
      .map(v => Number(v.trim()))
      .filter(v => !isNaN(v));
    return reportSequence;
  }

  isRestrictedReportSelection(selectedReportId: string | number | null | undefined, reports: IReportItem[]): boolean {
    if (selectedReportId == null || selectedReportId === CONSTANTS.EMPTY_STRING) {
      return false;
    }

    const selectedReport = reports.find((report) => String(report.id) === String(selectedReportId));
    if (!selectedReport) {
      return false;
    }

    const restrictedReportNames = new Set([
      'multi_pdf',
      'user_defined',
      'standard_library',
      'local_library'
    ]);

    return restrictedReportNames.has((selectedReport.reportName).trim().toLowerCase());
  }

  getVisibleReportItems(): IReportItem[] {
    const business = this.commonFunctionsService.getBusiness();
    return reportItems.filter(item => this.isReportVisibleForBusiness(item.reportName, business));
  }

  private isReportVisibleForBusiness(reportName: string, business: string): boolean {
    if (Object.values(this.checklistReportByBusiness).includes(reportName)) {
      return !!this.commonDataService.getEstimateData()
        && this.commonDataService.getReviewTabVisibility()
        && this.checklistReportByBusiness[business] === reportName;
    }

    switch (reportName) {
      case 'MATERIAL_CONTROLLERS':
        return business === CONSTANTS.BUSINESS_AUTOMATION || business === CONSTANTS.BUSINESS_MECHANICAL;
      case 'FIRE_ESTIMATING_ANALYTICS':
        return business === CONSTANTS.BUSINESS_FIRE;
      case 'TASK_BASED_LABOR_BY_AREA':
      case 'TASK_BASED_LABOR_SUMMARY':
        return business !== CONSTANTS.BUSINESS_AUTOMATION
          && business !== CONSTANTS.BUSINESS_ELECTRICAL
          && business !== CONSTANTS.BUSINESS_MECHANICAL;
      case 'ELECTRICAL_SERVICE_MATERIAL_HOURS': {
        const subSegmentId = this.commonDataService.getEstimateData()?.subSegmentID;
        const segmentId = this.commonMetaDataService.getSubSegmentDetails(subSegmentId)?.segmentId;
        return segmentId === CONSTANTS.FORTY_EIGHT || segmentId === CONSTANTS.FORTY_NINE;
      }
      default:
        return true;
    }
  }

  getMultiPdfDropdownItems(): ISubmittalPackages[] {
    const estimateCultureId = this.commonDataService.getEstimateCultureId();
    let standardPackage: MultiPdfTemplate[] = this.commonMetaDataService.getMetaData()?.multiPDFTemplates ?? [];
    let localPackage: MultiPdfTemplate[] = this.commonDataService.getUserData()?.multiPdfLocalLibraryReports?.multiPdfLocalLibraryReportsList ?? [];

    standardPackage = standardPackage.filter(pkg => pkg.reportTypeId === ReportsType.SOL_STANDARD && pkg.cultureId === estimateCultureId && !pkg.isItLocal);
    localPackage = localPackage.filter(pkg => pkg.reportTypeId === ReportsType.SOL_STANDARD && pkg.cultureId === estimateCultureId && pkg.isItLocal);

    return [
      { type: CONSTANTS.HEADER, name: this.translate.instant('MULTI_PDF'), disabled: true },
      { type: CONSTANTS.CATEGORY, name: this.translate.instant('USER_DEFINED'), disabled: true, indentLevel: 1 },
      { type: CONSTANTS.CATEGORY, name: this.translate.instant('STANDARD_LIBRARY'), disabled: true, indentLevel: 1 },
      ...standardPackage.map(pkg => ({ type: 'option', name: pkg.multiPdfSelection, id: pkg.id, disabled: false, indentLevel: 2 })),
      { type: CONSTANTS.CATEGORY, name: this.translate.instant('LOCAL_LIBRARY'), disabled: true, indentLevel: 1 },
      ...localPackage.map(pkg => ({ type: 'option', name: pkg.multiPdfSelection, id: pkg.id, disabled: false, indentLevel: 2 }))
    ];
  }

  private _reportCheckBoxChanged = new BehaviorSubject<ReportCheckboxes>({
    color: { checked: true, disabled: false, hidden: false },
    expanded: { checked: true, disabled: false, hidden: false },
    genericName: { checked: false, disabled: false, hidden: false }
  });
  readonly reportCheckBoxChanged$ = this._reportCheckBoxChanged.asObservable();
  private destroy$: Subject<void> = new Subject<void>();
  private genericNameDictionary: Record<string, string> = {};
  private selectedReportName: string = 'COST_SUMMARY_REPORT';
  private _selectedReport = new BehaviorSubject<IReportData>(null);
  readonly selectedReport$ = this._selectedReport.asObservable();

  // Subject for adding reports to Multi-PDF list (key = original report key, name = translated display name)
  private _addToMultiPdfList = new Subject<{ key: string; name: string }>();
  readonly addToMultiPdfList$ = this._addToMultiPdfList.asObservable();

  // Subject for Multi-PDF mode cancelled
  private _multiPdfCancelled = new Subject<void>();
  readonly multiPdfCancelled$ = this._multiPdfCancelled.asObservable();

  // Subject for requesting Multi-PDF generation (contains array of report keys)
  private _generateMultiPdf = new Subject<string[]>();
  readonly generateMultiPdf$ = this._generateMultiPdf.asObservable();

  // Subject for sending individual Multi-PDF report data (key + data)
  private _multiPdfReportData = new Subject<MultiPdfReportData>();
  readonly multiPdfReportData$ = this._multiPdfReportData.asObservable();

  // Subject for loading a report group (Standard Library or Local Library)
  private _loadReportGroup = new Subject<LoadReportGroupPayload>();
  readonly loadReportGroup$ = this._loadReportGroup.asObservable();
  private _lastMultiPdfState: LoadReportGroupPayload | null = null;
  private _lastStandardReportNameBeforeMultiPdf = '';

  // Subject for saving a custom report group to Local Library
  private _saveReportGroup = new Subject<SaveReportGroup>();
  readonly saveReportGroup$ = this._saveReportGroup.asObservable();

  // Local Library storage
  private _localLibraryGroups: MultiPdfReportGroup[] = [];

  // Standard Library groups (fetched from API)
  private _standardLibraryGroups: MultiPdfReportGroup[] = [];
  private _multiPdfTemplatesLoaded = false;

  // Flag to indicate if we're generating for Multi-PDF
  private _isMultiPdfGenerating = false;
  private _currentMultiPdfReportKey = '';

  // Subject for PDF buffer generation (byte array mode)
  private _pdfBufferReportData = new Subject<IReportData>();
  readonly pdfBufferReportData$ = this._pdfBufferReportData.asObservable();
  private _isPdfBufferGenerating = false;

  // Subject for notifying that user preferences were successfully saved
  private _preferencesSaved = new Subject<void>();
  readonly preferencesSaved$ = this._preferencesSaved.asObservable();

  // Subject for notifying that user cancelled switching Multi-PDF groups (contains previous group ID)
  private _selectPreviousMultiPDF = new Subject<string>();
  readonly selectPreviousMultiPDF$ = this._selectPreviousMultiPDF.asObservable();

  notifyPreferencesSaved(): void {
    this._preferencesSaved.next();
  }

  // Subject for notifying that a new estimate was opened
  private _estimateOpened = new Subject<void>();
  readonly estimateOpened$ = this._estimateOpened.asObservable();

  notifyEstimateOpened(): void {
    this._estimateOpened.next();
  }

  notifyReportSwitchCancelled(previousGrpId: string): void {
    this._selectPreviousMultiPDF.next(previousGrpId);
  }

  setGenericNameDictionary(response: GenericNameByCultureIDResponse): void {
    this.genericNameDictionary = response.genericNames;
  }

  getGenericNameDictionary(): Record<string, string> {
    return this.genericNameDictionary;
  }

  getGenericNameBySSN(key: string): string {
    return this.genericNameDictionary[key] || '';
  }

  isGenericNameApplicable(): boolean {
    return Object.keys(this.getGenericNameDictionary()).length > 0 &&
      this.getReportsCheckboxes().genericName.checked;
  }
  getInitialReportCheckboxes(): ReportCheckboxes {
    const pref = this.commonDataService.getPreferenceData()?.preferences;
    if (pref) {
      return {
        color: { checked: !!pref.showColorReport, disabled: false, hidden: false },
        expanded: { checked: !!pref.showExpanded, disabled: false, hidden: false },
        genericName: { checked: !!pref.showGenericName, disabled: false, hidden: false }
      };
    }
    return {
      color: { checked: true, disabled: false, hidden: false },
      expanded: { checked: true, disabled: false, hidden: false },
      genericName: { checked: false, disabled: false, hidden: false }
    };
  }

  getReportsCheckboxes() {
    const currentCheckboxes = this._reportCheckBoxChanged.value;
    const persistedCheckboxes = this.getInitialReportCheckboxes();

    // Keep in-memory report checkbox state aligned with persisted user preferences.
    // This ensures report generation uses restored values (for example genericName)
    // immediately after app reopen, even before any user interaction with the checkbox UI.
    if (!this.areReportCheckboxesEqual(currentCheckboxes, persistedCheckboxes)) {
      this._reportCheckBoxChanged.next(persistedCheckboxes);
      return persistedCheckboxes;
    }

    return currentCheckboxes;
  }

  getReportCheckboxesForReport(reportName?: string): ReportCheckboxes {
    const effectiveReportName = reportName ?? this.selectedReportName;
    const selectedItem = reportItems.find(item => item.reportName === effectiveReportName);
    const reportConfig = selectedItem ? REPORT_CHECKBOX_DISABLE_MAP[selectedItem.id] : undefined;
    const baseCheckboxes = this.getReportsCheckboxes();

    return {
      color: { ...baseCheckboxes.color },
      expanded: {
        ...baseCheckboxes.expanded,
        disabled: reportConfig?.expandedDisabled ?? false
      },
      genericName: {
        ...baseCheckboxes.genericName,
        disabled: reportConfig?.genericNameDisabled ?? false
      }
    };
  }

  private areReportCheckboxesEqual(current: ReportCheckboxes, persisted: ReportCheckboxes): boolean {
    if (!current || !persisted) {
      return false;
    }

    return current.color.checked === persisted.color.checked
      && current.expanded.checked === persisted.expanded.checked
      && current.genericName.checked === persisted.genericName.checked;
  }

  setReportCheckboxChanged(value: ReportCheckboxes) {
    const prefData = this.commonDataService.getPreferenceData()?.preferences;
    if (!prefData || prefData.showColorReport === undefined || prefData.showColorReport === null) {
      return;
    }

    const currentConfig = this.getReportCheckboxesForReport();
    const mergedValue: ReportCheckboxes = {
      color: {
        ...currentConfig.color,
        checked: value.color.checked
      },
      expanded: {
        ...currentConfig.expanded,
        checked: value.expanded.checked
      },
      genericName: {
        ...currentConfig.genericName,
        checked: value.genericName.checked
      }
    };

    prefData.showColorReport = mergedValue.color.checked;
    prefData.showExpanded = mergedValue.expanded.checked;
    prefData.showGenericName = mergedValue.genericName.checked;
    this._reportCheckBoxChanged.next(mergedValue);
   
   const req : SetUserPreferencesRequest={
    userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
    preferences: prefData,
    firePreferences: null,
    esPreferences: null,
    proposalPreferences: null,
    collaboratorPreferences: null,
    costReviewerPreferences: null,
    taxDetailsPreferences: null,
    resetPreferencesToDefault: false
   };
   this.preferenceApiService.setPreference(req).pipe(take(CONSTANTS.ONE)).subscribe();
  }

  getSelectedReportName(): string {
    return this.selectedReportName;
  }

  setSelectedReportName(reportName: string): void {
    this.selectedReportName = reportName;
  }

  setSelectedReport(report: IReportData) {
    if (!report) {
      return;
    }
    // Check if we're in PDF buffer generation mode
    if (this._isPdfBufferGenerating) {
      this._pdfBufferReportData.next(report);
      return;
    }
    // Check if we're in Multi-PDF generation mode
    if (this._isMultiPdfGenerating && this._currentMultiPdfReportKey) {
      // Send to Multi-PDF collection instead of displaying
      this.sendMultiPdfReportData(this._currentMultiPdfReportKey, report);
    } else {
      // Normal flow - display the report
      this._selectedReport.next(report);
    }
  }

  /**
   * Add a report to the Multi-PDF selected reports list
   * @param reportKey - Original report key (e.g., 'LABOR_HOURS_DISTRIBUTION')
   * @param translatedName - Translated display name
   */
  addReportToMultiPdfList(reportKey: string, translatedName: string): void {
    this._addToMultiPdfList.next({ key: reportKey, name: translatedName });
  }

  /**
   * Request generation of Multi-PDF with the given report keys
   */
  requestMultiPdfGeneration(reportKeys: string[]): void {
    this._generateMultiPdf.next(reportKeys);
  }

  /**
   * Start Multi-PDF generation mode
   */
  startMultiPdfGeneration(reportKey: string): void {
    this._isMultiPdfGenerating = true;
    this._currentMultiPdfReportKey = reportKey;
  }

  /**
   * End Multi-PDF generation mode
   */
  endMultiPdfGeneration(): void {
    this._isMultiPdfGenerating = false;
    this._currentMultiPdfReportKey = '';
  }

  /**
   * Start PDF buffer generation mode
   */
  startPdfBufferGeneration(): void {
    this._isPdfBufferGenerating = true;
  }

  /**
   * End PDF buffer generation mode
   */
  endPdfBufferGeneration(): void {
    this._isPdfBufferGenerating = false;
  }

  /**
   * Check if currently generating for Multi-PDF
   */
  isMultiPdfGenerating(): boolean {
    return this._isMultiPdfGenerating;
  }

  /**
   * Get current Multi-PDF report key
   */
  getCurrentMultiPdfReportKey(): string {
    return this._currentMultiPdfReportKey;
  }

  /**
   * Send Multi-PDF report data to be collected
   */
  sendMultiPdfReportData(reportKey: string, reportData: IReportData): void {
    this._multiPdfReportData.next({ reportKey, reportData });
  }

  /**
   * Notify that Multi-PDF mode was cancelled
   */
  notifyMultiPdfCancelled(): void {
    this.clearLastMultiPdfState();
    this._multiPdfCancelled.next();
  }

  /**
   * Get Standard Library groups (populated from API)
   */
  getStandardLibraryGroups(): MultiPdfReportGroup[] {
    return this._standardLibraryGroups;
  }

  /**
   * Get a Standard Library group by name
   */
  getStandardLibraryGroup(name: string): MultiPdfReportGroup | undefined {
    return this._standardLibraryGroups.find(group => group.name === name);
  }

  /**
   * Check if multi-PDF templates have been loaded from API
   */
  isMultiPdfTemplatesLoaded(): boolean {
    return this._multiPdfTemplatesLoaded;
  }

  /**
   * Fetch multi-PDF templates from the API and populate standard/local library groups.
   * @param userId - The current user's ID
   * @param cultureId - Culture ID (default 1 for en-US)
   * @returns Promise that resolves when templates are loaded
   */
  fetchMultiPdfTemplates(): void {

    try {
      const response = this.commonMetaDataService.getMetaData()?.multiPDFTemplates.filter(x => x.cultureId === this.commonDataService.getEstimateCultureId()) ?? [];
      this.processMultiPdfTemplates(response || []);
    } catch {
      this._standardLibraryGroups = [];
    } finally {
      this._multiPdfTemplatesLoaded = true;
    }
  }

  /**
   * Process API response and segregate templates into standard/local library groups
   */
  private processMultiPdfTemplates(templates: MultiPdfTemplate[]): void {
    this._standardLibraryGroups = [];
    const localGroups: MultiPdfReportGroup[] = [];

    for (const template of templates) {
      const group: MultiPdfReportGroup = {
        id: template.id,
        name: template.multiPdfSelection,
        description: template.multiPdfDescription,
        reportKeys: reportSequenceToKeys(template.reportSequence),
        reportSequenceIds: reportSequenceToIds(template.reportSequence),
        type: template.isItLocal ? 'local' : 'standard',
        modifiedAt: template.lastModifiedDate ? new Date(template.lastModifiedDate) : undefined
      };

      if (template.isItLocal) {
        localGroups.push(group);
      } else {
        this._standardLibraryGroups.push(group);
      }
    }

    // Merge fetched local groups with any in-memory local groups (user-defined in session)
    for (const group of localGroups) {
      const existingIdx = this._localLibraryGroups.findIndex(g => g.id === group.id);
      if (existingIdx === -1) {
        this._localLibraryGroups.push(group);
      }
    }
  }

  /**
   * Load a Standard Library group - sends reports to iframe
   * @param groupName - Name of the Standard Library group
   * @param translateFn - Function to translate report keys to display names
   */
  loadStandardLibraryGroup(groupName: string, translateFn: (key: string) => string): void {
    const group = this.getStandardLibraryGroup(groupName);
    if (group) {
      const reports = group.reportKeys.map(key => ({
        key,
        name: translateFn(key)
      }));
      const payload: LoadReportGroupPayload = { reports, groupName, groupType: 'standard', description: group.description || '' };
      this._loadReportGroup.next(payload);
      return;
    }
  }

  /**
   * Get Local Library groups
   */
  getLocalLibraryGroups(): MultiPdfReportGroup[] {
    return this._localLibraryGroups;
  }

  /**
   * Load a Local Library group - sends reports to iframe
   * @param groupName - Name of the Local Library group
   * @param translateFn - Function to translate report keys to display names
   */
  loadLocalLibraryGroup(groupName: string, translateFn: (key: string) => string): void {
    const group = this._localLibraryGroups.find(g => g.name === groupName);
    if (group) {
      const reports = group.reportKeys.map(key => ({
        key,
        name: translateFn(key)
      }));
      const payload: LoadReportGroupPayload = { reports, groupName, groupType: 'local', description: group.description || '' };
      this._loadReportGroup.next(payload);
      return;
    }
  }

  /**
   * Load User Defined mode (empty list)
   */
  loadUserDefinedMode(groupName: string = MULTI_PDF_REPORT_KEYS.USER_DEFINED): void {
    const savedState = this.getLastMultiPdfState();
    const payload: LoadReportGroupPayload = savedState?.groupType === 'user-defined'
      ? { ...savedState, groupName }
      : { reports: [], groupName, groupType: 'user-defined' };
    this._loadReportGroup.next(payload);
  }

  setLastMultiPdfState(state: LoadReportGroupPayload): void {
    if (!state) {
      this._lastMultiPdfState = null;
      return;
    }

    this._lastMultiPdfState = {
      reports: (state.reports ?? []).map(report => ({ key: report.key, name: report.name })),
      groupName: state.groupName,
      groupType: state.groupType,
      description: state.description ?? '',
      hasChanges: state.hasChanges ?? false
    };
  }

  getLastMultiPdfState(): LoadReportGroupPayload | null {
    if (!this._lastMultiPdfState) {
      return null;
    }

    return {
      reports: this._lastMultiPdfState.reports.map(report => ({ key: report.key, name: report.name })),
      groupName: this._lastMultiPdfState.groupName,
      groupType: this._lastMultiPdfState.groupType,
      description: this._lastMultiPdfState.description ?? '',
      hasChanges: this._lastMultiPdfState.hasChanges ?? false
    };
  }

  clearLastMultiPdfState(): void {
    this._lastMultiPdfState = null;
  }

  setLastStandardReportNameBeforeMultiPdf(reportName: string): void {
    this._lastStandardReportNameBeforeMultiPdf = reportName || '';
  }

  getLastStandardReportNameBeforeMultiPdf(): string {
    return this._lastStandardReportNameBeforeMultiPdf;
  }

  /**
   * Save a custom report group to Local Library
   * @param groupName - Name for the new group
   * @param reportKeys - Array of report keys
   * @param description - Optional description for the group
   */
  /**
   * Save a custom report group to Local Library (Save As - creates new or overwrites existing by name)
   * Calls saveOrSaveAsMultiPdfReport API
   * @param groupName - Name for the new group
   * @param reportKeys - Array of report keys
   * @param description - Optional description for the group
   */
  async saveToLocalLibrary(groupName: string, reportKeys: string[], description?: string): Promise<void> {
    const existingIndex = this._localLibraryGroups.findIndex(g => g.name === groupName);
    const templateId = existingIndex !== -1 ? this._localLibraryGroups[existingIndex].id : '';

    const request: SaveOrSaveAsMultiPdfTemplateRequest = {
      userId: CONSTANTS.EMPTY_STRING,
      templateId,
      multiPdfSelection: groupName,
      multiPdfDescription: description || '',
      reportSequence: reportKeysToSequence(reportKeys),
      reportTypeId: ReportsType.SOL_STANDARD,
      cultureId: CONSTANTS.ONE
    };

    try {
      const response = await firstValueFrom(this.reportsApiService.saveOrSaveAsMultiPdfReport(request));
      const savedTemplate = response.savedTemplate;

      const newGroup: MultiPdfReportGroup = {
        id: savedTemplate.id,
        name: savedTemplate.multiPdfSelection,
        description: savedTemplate.multiPdfDescription,
        reportKeys: reportSequenceToKeys(savedTemplate.reportSequence),
        reportSequenceIds: reportSequenceToIds(savedTemplate.reportSequence),
        type: 'local',
        createdAt: existingIndex === -1 ? new Date() : this._localLibraryGroups[existingIndex].createdAt,
        modifiedAt: savedTemplate.lastModifiedDate ? new Date(savedTemplate.lastModifiedDate) : new Date()
      };

      if (existingIndex !== -1) {
        this._localLibraryGroups[existingIndex] = newGroup;
      } else {
        this._localLibraryGroups.push(newGroup);
      }

      // Notify subscribers about the save
      this._saveReportGroup.next({ groupName, reports: reportKeys.map(key => ({ key, name: key })) });
    } catch {
      // Re-throw so callers can handle the error
      throw new Error('Failed to save multi-PDF template');
    }
  }

  /**
   * Update an existing Local Library group with new reports
   * Calls saveOrSaveAsMultiPdfReport API with existing templateId
   * @param groupName - Name of the group to update
   * @param reportKeys - New array of report keys
   * @param description - Optional updated description for the group
   */
  async updateLocalLibraryGroup(groupName: string, reportKeys: string[], description?: string): Promise<void> {
    const existingIndex = this._localLibraryGroups.findIndex(g => g.name === groupName);
    if (existingIndex === -1) {
      return;
    }

    const existingGroup = this._localLibraryGroups[existingIndex];

    const request: SaveOrSaveAsMultiPdfTemplateRequest = {
      userId: CONSTANTS.EMPTY_STRING,
      templateId: existingGroup.id,
      multiPdfSelection: groupName,
      multiPdfDescription: description ?? existingGroup.description ?? '',
      reportSequence: reportKeysToSequence(reportKeys),
      reportTypeId: ReportsType.SOL_STANDARD,
      cultureId: CONSTANTS.ONE
    };

    try {
      const response = await firstValueFrom(this.reportsApiService.saveOrSaveAsMultiPdfReport(request));
      const savedTemplate = response.savedTemplate;

      this._localLibraryGroups[existingIndex] = {
        ...existingGroup,
        description: savedTemplate.multiPdfDescription,
        reportKeys: reportSequenceToKeys(savedTemplate.reportSequence),
        reportSequenceIds: reportSequenceToIds(savedTemplate.reportSequence),
        modifiedAt: savedTemplate.lastModifiedDate ? new Date(savedTemplate.lastModifiedDate) : new Date()
      };
    } catch {
      throw new Error('Failed to update multi-PDF template');
    }
  }

  /**
   * Delete a Local Library group
   * Calls deleteMultiPdfReport API
   * @param groupName - Name of the group to delete
   */
  async deleteLocalLibraryGroup(groupName: string): Promise<void> {
    const group = this._localLibraryGroups.find(g => g.name === groupName);
    if (!group) {
      return;
    }

    const request: DeleteMultiPdfTemplateRequest = {
      userId: CONSTANTS.EMPTY_STRING,
      templateId: group.id
    };

    try {
      await firstValueFrom(this.reportsApiService.deleteMultiPdfReport(request));
      this._localLibraryGroups = this._localLibraryGroups.filter(g => g.name !== groupName);
    } catch {
      throw new Error('Failed to delete multi-PDF template');
    }
  }

  getGenericNameDictionaryByCultureId(): Promise<void> {
    return new Promise((resolve) => {
      if (Object.keys(this.getGenericNameDictionary()).length <= 0 &&
        this.getReportsCheckboxes().genericName.checked) {

        const genericNameRequest: GenericNameByCultureIDRequest = this.createGenericNameRequest();

        this.reportsApiService.getGenericNameByCultureId(genericNameRequest)
          .pipe(take(CONSTANTS.ONE))
          .subscribe({
            next: (response: GenericNameByCultureIDResponse) => {
              this.setGenericNameDictionary(response);
              resolve();
            },
            error: () => {
              this.setGenericNameDictionary({ genericNames: {} });
              resolve();
            }
          });

      } else {
        resolve();
      }
    });
  }

  createGenericNameRequest(): GenericNameByCultureIDRequest {
    let request: GenericNameByCultureIDRequest = {
      cultureID: this.commonDataService.getEstimateData()?.cultureID ?? CONSTANTS.ONE
    }
    return request;
  }

  normalizedConfiguredOrigins(): string[] {
    const configuredOrigins = environment.reportMessaging?.allowedOrigins ?? [];
    return configuredOrigins
      .filter((origin) => !!origin && origin !== '*')
      .map((origin) => origin.replace(/\/$/, ''));
  }

  /**
   * Send a message to iframe contentWindow - extracted for better testability
   * @param contentWindow - The iframe's contentWindow
   * @param message - The message object to send
  */
  sendMessageToIframe(contentWindow: Window, message: ReportEnginePayload): void {
    const normalizedConfiguredOrigins = this.normalizedConfiguredOrigins();

    const allowedOrigins = normalizedConfiguredOrigins.length > 0
      ? normalizedConfiguredOrigins
      : [location.origin];

    // Iframe is served from a relative path, so it always shares the current window's origin.
    const resolvedOrigin = location.origin.replace(/\/$/, '');

    if (!allowedOrigins.includes(resolvedOrigin)) {
      return;
    }

    contentWindow.postMessage(message, resolvedOrigin);
  }


  generateMaterialIndicator(material: Material): MaterialIndicators {
    const preferenceData: Preferences = this.commonDataService.getPreferenceData()?.preferences;
    if (!material || !preferenceData) {
      return null;
    }

    if (material.isRPN) {
      if (material.referenceNumber === CONSTANTS.USER_DEFINED_REFERENCE_NUMBER && material.guid && preferenceData.showUPNMaterialIndicator) {
        return CONSTANTS.UPN_INDICATOR;
      } else if (!material.guid && preferenceData.showRPNMaterialIndicator) {
        return CONSTANTS.RPN_INDICATOR;
      }
    } else {
      const isEpartsMaterial = material instanceof EPartsMaterial;
      if (isEpartsMaterial && material.guid && preferenceData.showePartsMaterialIndicator) {
        return CONSTANTS.SIPART_INDICATOR;
      } else if (isEpartsMaterial && !material.guid && preferenceData.showUPNMaterialIndicator) {
        return CONSTANTS.UPN_INDICATOR;
      } else if (!isEpartsMaterial && preferenceData.showAPNMaterialIndicator) {
        return CONSTANTS.APN_INDICATOR;
      }
    }
    return null;
  }

  pushSubmittalPackageData(): void {
    const cultureId = this.commonDataService.getEstimateCultureId?.() ?? CONSTANTS.ONE;
    const metaData = this.commonMetaDataService.getMetaData();
    if(metaData){
      const otherStandardPackages = (metaData.submittalPackages ?? []).filter(pkg => !(pkg.reportTypeID === ReportsType.SOL_STANDARD
                && pkg.cultureID === cultureId
                && !pkg.isItLocal));
      const standardPackages : SubmittalPackage[] = this._standardLibraryGroups.map(group => ({
        id: group.id,
        name: group.name,
        description: group.description ?? '',
        reportSequence: reportKeysToSequence(group.reportKeys),
        reportTypeID: ReportsType.SOL_STANDARD,
        cultureID: cultureId,
        isItLocal: false,
      } as SubmittalPackage));
      metaData.submittalPackages = [ ...otherStandardPackages, ...standardPackages];
    }
  }
  pushLocalLibraryReportData(): void {
    const cultureId = this.commonDataService.getEstimateCultureId?.() ?? CONSTANTS.ONE;
    const userData = this.commonDataService.getUserData();
    if(userData){
    userData.multiPdfLocalLibraryReports = userData.multiPdfLocalLibraryReports ?? {
      multiPdfLocalLibraryReportsList: []
    };
    const list = userData.multiPdfLocalLibraryReports.multiPdfLocalLibraryReportsList ?? [];
    const otherLocalReports = list.filter(pkg => !(pkg.reportTypeId === ReportsType.SOL_STANDARD
                && pkg.cultureId === cultureId
                && pkg.isItLocal));
    const localPackages: MultiPdfTemplate[] = this._localLibraryGroups.map(g => ({
            id: g.id,
            multiPdfSelection: g.name,
            multiPdfDescription: g.description ?? '',
            reportSequence: reportKeysToSequence(g.reportKeys),
            reportTypeId: ReportsType.SOL_STANDARD,
            cultureId: cultureId,
            isItLocal: true,
            isActive: CONSTANTS.ONE,
            lastModifiedDate: g.modifiedAt ?? new Date()
        } as MultiPdfTemplate));
    userData.multiPdfLocalLibraryReports.multiPdfLocalLibraryReportsList = [ ...otherLocalReports, ...localPackages];
    }
  }
  pushMultiPdfReportData(): void {
    this.pushSubmittalPackageData();
    this.pushLocalLibraryReportData(); 
}


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._reportCheckBoxChanged.complete();
    this._selectedReport.complete();
    this._addToMultiPdfList.complete();
    this._multiPdfCancelled.complete();
    this._generateMultiPdf.complete();
    this._multiPdfReportData.complete();
    this._loadReportGroup.complete();
    this._saveReportGroup.complete();
    this._pdfBufferReportData.complete();
    this._preferencesSaved.complete();
    this._estimateOpened.complete();
    this._selectPreviousMultiPDF.complete();
  }

  /**
   * Resets all estimate-scoped report state.
   * Called when the user closes an estimate/project.
   * Does NOT complete Subjects (service is a root singleton that outlives estimates).
   */
  resetEstimateState(): void {
    this.genericNameDictionary = {};
    this.selectedReportName = 'COST_SUMMARY_REPORT';
    this._reportCheckBoxChanged.next({
      color: { checked: true, disabled: false, hidden: false },
      expanded: { checked: true, disabled: false, hidden: false },
      genericName: { checked: false, disabled: false, hidden: false }
    });
    this._selectedReport.next(null);
    this._localLibraryGroups = [];
    this._standardLibraryGroups = [];
    this._multiPdfTemplatesLoaded = false;
    this._isMultiPdfGenerating = false;
    this._currentMultiPdfReportKey = '';
    this._isPdfBufferGenerating = false;
    this._lastMultiPdfState = null;
    this._lastStandardReportNameBeforeMultiPdf = '';
  }

}
